import type { ConditionEvaluator } from "../../types/conditionEvaluators.ts";
import type {
  BooleanArrayCondition,
  IBooleanArrayBinaryCondition,
  IBooleanArrayTernaryCondition,
} from "../../types/conditions/arrays/booleans.ts";
import { Operator } from "../../types/operators.ts";
import { createThrowConditionNotImplementedForEvaluator, getArrayValues, getSingleValue } from "../common.ts";

const throwNotImplemented = createThrowConditionNotImplementedForEvaluator("boolean[]");

const binaryConditionEvaluator: ConditionEvaluator<IBooleanArrayBinaryCondition> = (condition, resolve): boolean => {
  const subjectValues = getArrayValues("boolean", condition.subject, resolve);
  const objectValue = getSingleValue("boolean", condition.object, resolve);

  switch (condition.operator) {
    case Operator.Has:
      return subjectValues.indexOf(objectValue) > -1;

    case Operator.NotHas:
      return subjectValues.indexOf(objectValue) === -1;

    case Operator.First:
      return subjectValues.indexOf(objectValue) === 0;

    case Operator.Last:
      return subjectValues.indexOf(objectValue) === subjectValues.length - 1;

    default:
      return throwNotImplemented(condition);
  }
};

const ternaryConditionEvaluator: ConditionEvaluator<IBooleanArrayTernaryCondition> = (condition, resolve): boolean => {
  const subjectValues = getArrayValues("boolean", condition.subject, resolve);
  const left = getSingleValue("boolean", condition.left, resolve);
  const right = getSingleValue("boolean", condition.right, resolve);

  const indexLeft = subjectValues.indexOf(left);
  const indexRight = subjectValues.indexOf(right);

  switch (condition.operator) {
    case Operator.Before:
      if (indexLeft === -1 || indexRight === -1) {
        return false;
      }
      return indexLeft < indexRight;

    case Operator.After:
      if (indexLeft === -1 || indexRight === -1) {
        return false;
      }
      return indexLeft > indexRight;

    default:
      return throwNotImplemented(condition);
  }
};

export const conditionEvaluator: ConditionEvaluator<BooleanArrayCondition> = (condition, resolve): boolean => {
  switch (condition.operator) {
    case Operator.Has:
    case Operator.NotHas:
    case Operator.First:
    case Operator.Last:
      return binaryConditionEvaluator(condition, resolve);

    case Operator.Before:
    case Operator.After:
      return ternaryConditionEvaluator(condition, resolve);

    default:
      return throwNotImplemented(condition);
  }
};

export default conditionEvaluator;
